import { apiService } from './api';
import {
  Notification,
  NotificationListRequest,
  NotificationListResponse,
  NotificationCount,
  GroupedNotificationResponse,
  UserNotificationPreference,
  UpdateNotificationPreferenceRequest,
} from '../types/workflow';

/**
 * Service for managing user notifications.
 * Handles in-app notifications for workflow events, team assignments, and SLA alerts.
 */
export const notificationService = {
  // ==========================================================================
  // NOTIFICATION RETRIEVAL
  // ==========================================================================

  /**
   * Get paginated list of notifications with filtering.
   * @param request Pagination and filter parameters
   */
  getNotifications: async (request: NotificationListRequest = {}): Promise<NotificationListResponse> => {
    const params = new URLSearchParams();

    if (request.pageNumber) params.append('pageNumber', request.pageNumber.toString());
    if (request.pageSize) params.append('pageSize', request.pageSize.toString());
    if (request.isRead !== undefined) params.append('isRead', request.isRead.toString());
    if (request.type) params.append('type', request.type);

    const queryString = params.toString();
    const url = queryString ? `/notifications?${queryString}` : '/notifications';

    const response = await apiService.get<NotificationListResponse>(url);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch notifications');
    }

    return response.data;
  },

  /**
   * Get count of unread and total notifications.
   */
  getCount: async (): Promise<NotificationCount> => {
    const response = await apiService.get<NotificationCount>('/notifications/count');

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch notification count');
    }

    return response.data;
  },

  // ==========================================================================
  // NOTIFICATION ACTIONS
  // ==========================================================================

  /**
   * Mark a specific notification as read.
   * @param id The notification ID
   */
  markAsRead: async (id: number): Promise<Notification> => {
    const response = await apiService.put<Notification>(`/notifications/${id}/read`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to mark notification as read');
    }

    return response.data;
  },

  /**
   * Mark all notifications as read.
   */
  markAllAsRead: async (): Promise<void> => {
    const response = await apiService.put<void>('/notifications/read-all');

    if (!response.success) {
      throw new Error(response.message || 'Failed to mark all notifications as read');
    }
  },

  /**
   * Delete a specific notification.
   * @param id The notification ID
   */
  delete: async (id: number): Promise<void> => {
    const response = await apiService.delete<void>(`/notifications/${id}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete notification');
    }
  },

  // ==========================================================================
  // CONVENIENCE METHODS
  // ==========================================================================

  /**
   * Get only unread notifications.
   * @param pageSize Optional page size (default 10)
   */
  getUnread: async (pageSize: number = 10): Promise<NotificationListResponse> => {
    return notificationService.getNotifications({
      isRead: false,
      pageSize
    });
  },

  /**
   * Get notifications by type.
   * @param type The notification type (e.g., "WorkflowTransition", "SlaWarning")
   * @param pageSize Optional page size (default 20)
   */
  getByType: async (type: string, pageSize: number = 20): Promise<NotificationListResponse> => {
    return notificationService.getNotifications({
      type,
      pageSize
    });
  },

  /**
   * Check if there are any unread notifications.
   */
  hasUnread: async (): Promise<boolean> => {
    const count = await notificationService.getCount();
    return count.unreadCount > 0;
  },

  // ==========================================================================
  // GROUPED & PREFERENCES
  // ==========================================================================

  /**
   * Get notifications grouped by priority tier (action / SLA / updates).
   */
  getGrouped: async (): Promise<GroupedNotificationResponse> => {
    const response = await apiService.get<GroupedNotificationResponse>('/notifications/grouped');
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch grouped notifications');
    }
    return response.data;
  },

  /**
   * Get user notification preferences.
   */
  getPreferences: async (): Promise<UserNotificationPreference> => {
    const response = await apiService.get<UserNotificationPreference>('/notification-preferences');
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch notification preferences');
    }
    return response.data;
  },

  /**
   * Update user notification preferences.
   */
  updatePreferences: async (request: UpdateNotificationPreferenceRequest): Promise<UserNotificationPreference> => {
    const response = await apiService.put<UserNotificationPreference>('/notification-preferences', request);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update notification preferences');
    }
    return response.data;
  },

  /**
   * Mark a batch of notifications as read by IDs.
   */
  markBatchAsRead: async (notificationIds: number[]): Promise<boolean> => {
    const response = await apiService.put<boolean>('/notifications/batch-read', { notificationIds });
    return response.success;
  },
};
